import React, { useState, useEffect, useRef } from "react";
import api from "@/lib/api";

/**
 * Work zones picker: online (checkbox) + regioni (multi-select) + comuni (autocomplete multi).
 * value: { zone_online: bool, zone_regioni: string[], zone_comuni: string[] }
 */
export default function WorkZonesPicker({ value, onChange }) {
  const [regioni, setRegioni] = useState([]);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => { api.get("/italy/regioni").then((r) => setRegioni(r.data)); }, []);

  useEffect(() => {
    if (!query || query.length < 2) { setSuggestions([]); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/italy/comuni?q=${encodeURIComponent(query)}&limit=12`);
        setSuggestions(data);
      } catch { setSuggestions([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const toggleRegion = (r) => {
    const curr = value.zone_regioni || [];
    const next = curr.includes(r) ? curr.filter((x) => x !== r) : [...curr, r];
    onChange({ ...value, zone_regioni: next });
  };
  const addComune = (c) => {
    const curr = value.zone_comuni || [];
    if (curr.includes(c.label)) return;
    onChange({ ...value, zone_comuni: [...curr, c.label] });
    setQuery(""); setSuggestions([]);
  };
  const removeComune = (label) => {
    onChange({ ...value, zone_comuni: (value.zone_comuni || []).filter((x) => x !== label) });
  };

  return (
    <div className="space-y-4" data-testid="work-zones-picker">
      {/* ONLINE */}
      <label className="flex items-center gap-3 p-4 hm-card cursor-pointer hover:border-[#FFBE00] transition-colors">
        <input type="checkbox" checked={!!value.zone_online} onChange={(e) => onChange({ ...value, zone_online: e.target.checked })} className="w-4 h-4 accent-[#FFBE00]" data-testid="zone-online-check" />
        <span className="flex-1">
          <span className="font-heading font-bold text-sm flex items-center gap-2"><i className="fas fa-globe text-[#FFBE00]"></i> Online in tutta Italia</span>
          <span className="text-xs text-zinc-500 block mt-0.5">Il professionista offre il servizio a distanza, senza limiti geografici.</span>
        </span>
      </label>

      {/* REGIONI */}
      <div>
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Regioni operative</div>
        <div className="flex flex-wrap gap-2" data-testid="zone-regioni">
          {regioni.map((r) => {
            const active = (value.zone_regioni || []).includes(r);
            return (
              <button
                type="button"
                key={r}
                onClick={() => toggleRegion(r)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${active ? "bg-[#E619A8] text-white" : "bg-[#0A0A0A] border border-zinc-800 text-zinc-300 hover:border-[#E619A8]"}`}
                data-testid={`zone-regione-${r}`}
              >
                {active && <i className="fas fa-check mr-1 text-[10px]"></i>}{r}
              </button>
            );
          })}
        </div>
      </div>

      {/* COMUNI */}
      <div ref={boxRef}>
        <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Comuni specifici</div>
        {(value.zone_comuni || []).length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {value.zone_comuni.map((c) => (
              <span key={c} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#FFBE00] text-black text-xs font-bold" data-testid={`zone-comune-chip-${c}`}>
                {c}
                <button type="button" onClick={() => removeComune(c)} className="ml-1 hover:text-white">×</button>
              </span>
            ))}
          </div>
        )}
        <div className="relative">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setTimeout(() => setFocused(false), 200)}
            placeholder="Cerca comune (es. Genova, Milano…)"
            className="w-full bg-[#0A0A0A] border border-zinc-800 rounded-md px-3 py-2 text-sm focus:border-[#FFBE00] outline-none"
            data-testid="zone-comuni-input"
          />
          {focused && (suggestions.length > 0 || loading) && (
            <div className="absolute z-20 mt-1 w-full bg-[#0A0A0A] border border-zinc-800 rounded-md shadow-lg max-h-64 overflow-y-auto">
              {loading && <div className="px-3 py-2 text-xs text-zinc-500">Cerco…</div>}
              {suggestions.map((c) => (
                <button
                  type="button"
                  key={`${c.nome}-${c.sigla}`}
                  onClick={() => addComune(c)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-[#141414] flex items-center justify-between"
                >
                  <span><i className="fas fa-location-dot text-[#E619A8] text-[10px] mr-2"></i>{c.label}</span>
                  <span className="text-[10px] uppercase tracking-widest text-zinc-500">{c.regione}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* summary */}
      {(value.zone_online || (value.zone_regioni || []).length > 0 || (value.zone_comuni || []).length > 0) && (
        <div className="text-xs text-zinc-500 border-l-2 border-[#FFBE00] pl-3">
          {value.zone_online && <div><i className="fas fa-check text-[#22C55E] mr-1"></i>Online in tutta Italia</div>}
          {(value.zone_regioni || []).length > 0 && <div><i className="fas fa-check text-[#22C55E] mr-1"></i>{value.zone_regioni.length} region{value.zone_regioni.length === 1 ? 'e' : 'i'}</div>}
          {(value.zone_comuni || []).length > 0 && <div><i className="fas fa-check text-[#22C55E] mr-1"></i>{value.zone_comuni.length} comun{value.zone_comuni.length === 1 ? 'e' : 'i'}</div>}
        </div>
      )}
    </div>
  );
}
